import { numberHelpers } from '@approvalmax/utils';
import { useCallback, useMemo } from 'react';
import { useFormContext, useWatch } from 'react-hook-form';
import { Role } from 'shared/data/walletsKyc/v1';
import { z } from 'zod';

import { getFullName } from '../../../../personsBasic/resources/components/PersonsTable/PersonsTable.helpers';
import { schema } from '../../../resources/schema';
import type { EnrichedPerson } from './SettingsTable.types';

export const usePersons = () => {
    const { setValue } = useFormContext<z.infer<typeof schema>>();
    const persons = useWatch<z.infer<typeof schema>, 'people.persons'>({ name: 'people.persons' });

    const uncheckOtherPersons = useCallback(
        (person: EnrichedPerson) => {
            persons.forEach((otherPerson, index) => {
                if (index !== person.index) {
                    if (person.roles.some((role) => otherPerson.roles.includes(role))) {
                        setValue(`people.persons.${index}.verifyNeeded`, false);
                        setValue(`people.persons.${index}.isWalletOwner`, false);
                    }
                }
            });
        },
        [persons, setValue]
    );

    const handleCheckVerifyNeeded = useCallback(
        (person: EnrichedPerson, checked: boolean) => {
            if (person.roles.includes(Role.director)) {
                setValue(`people.persons.${person.index}.isWalletOwner`, checked);

                if (checked) {
                    uncheckOtherPersons(person);
                }
            }
        },
        [setValue, uncheckOtherPersons]
    );

    const handleCheckIsWalletOwner = useCallback(
        (person: EnrichedPerson, checked: boolean) => {
            setValue(`people.persons.${person.index}.verifyNeeded`, checked);

            if (checked) {
                uncheckOtherPersons(person);
            }
        },
        [setValue, uncheckOtherPersons]
    );

    const { directorVerifyNeededIndex, ownerVerifyNeededIndex, isWalletOwnerIndex } = useMemo(
        () =>
            (persons ?? []).reduce<{
                directorVerifyNeededIndex: null | number;
                ownerVerifyNeededIndex: null | number;
                isWalletOwnerIndex: null | number;
            }>(
                (acc, person, index) => {
                    if (person.roles.includes(Role.director) && person.verifyNeeded) {
                        acc.directorVerifyNeededIndex = index;
                    }

                    if (person.roles.includes(Role.ultimate_business_owner) && person.verifyNeeded) {
                        acc.ownerVerifyNeededIndex = index;
                    }

                    if (person.isWalletOwner) {
                        acc.isWalletOwnerIndex = index;
                    }

                    return acc;
                },
                { directorVerifyNeededIndex: null, ownerVerifyNeededIndex: null, isWalletOwnerIndex: null }
            ),
        [persons]
    );

    return useMemo(
        () =>
            (persons ?? []).map((person, index) => ({
                id: person.personId,
                index,
                name: getFullName(person),
                verifyNeededDisabled:
                    (person.roles.includes(Role.director) &&
                        numberHelpers.isNumber(directorVerifyNeededIndex) &&
                        directorVerifyNeededIndex !== index) ||
                    (person.roles.length === 1 &&
                        person.roles.includes(Role.ultimate_business_owner) &&
                        numberHelpers.isNumber(ownerVerifyNeededIndex) &&
                        ownerVerifyNeededIndex !== index),
                isWalletOwnerDisabled:
                    !person.roles.includes(Role.director) ||
                    (numberHelpers.isNumber(isWalletOwnerIndex) && index !== isWalletOwnerIndex),
                onCheckVerifyNeeded: handleCheckVerifyNeeded,
                onCheckIsWalletOwner: handleCheckIsWalletOwner,
                ...person,
            })),
        [
            directorVerifyNeededIndex,
            persons,
            handleCheckIsWalletOwner,
            handleCheckVerifyNeeded,
            isWalletOwnerIndex,
            ownerVerifyNeededIndex,
        ]
    );
};
